import { useAccount } from 'wagmi'
import { formatAmount } from '~/components/Exchange/helpers'
import { Column } from '~/components/Layout'
import { OrderedLimitSwapToken } from '~/components/LimitOrder/hooks/useOrderedTokenList'
import TokenLogo from '~/components/TokenLogo'
import { truncateAddress } from '~/utils/truncateAddress'
import { useUpdaterChainId } from '~/hooks'
import { ReactComponent as IconAdd } from '~/assets/images/add-round-black.svg'
import { formatFormAmount } from '~/utils/numbers'
import { getNetworkLogo } from '~/utils/chain'
import styled from 'styled-components'

const TokenListItem: React.FC<{
  token: OrderedLimitSwapToken
  showNetworkBadge: boolean
  onSelect: (token: ERC20Token) => void
  showContractAddr?: boolean
  custom?: boolean
}> = ({ token, showNetworkBadge, onSelect, showContractAddr, custom }) => {
  const {
    chainId: tokenChainId,
    logo,
    symbol,
    balance,
    priceUSD,
    address,
    displayType,
    name,
  } = token
  const { chainId, address: account } = useAccount()
  const expectChainId = useUpdaterChainId()
  const isWrongChain = Number(chainId) !== Number(expectChainId)
  const addByUser = Number(displayType) === 999
  const networkLogo = getNetworkLogo(tokenChainId)

  return (
    <ListItem onClick={() => onSelect(token)}>
      <TokenLogo
        large
        logo={logo}
        symbol={symbol}
        space={0}
        networkLogo={networkLogo}
        showNetworkBadge={showNetworkBadge}
      />
      <div className="item-right">
        <Column>
          <span
            data-testid="limitorder-token-list-item-symbol"
            className="token-symbol"
          >
            {symbol}
          </span>
          <div>
            {name ? (
              <span
                data-testid="limitorder-token-list-item-name"
                className="token-name"
              >
                {name}{' '}
              </span>
            ) : null}
            {(addByUser || name) && showContractAddr ? <span> · </span> : null}
            {showContractAddr ? (
              <span className="token-address">{truncateAddress(address)}</span>
            ) : null}
            {addByUser && (showContractAddr || name) ? <span> · </span> : null}
            {addByUser ? (
              <span className="token-user-add">
                <span>Add by user</span>
              </span>
            ) : null}
          </div>
        </Column>
        {account && !custom && !isWrongChain && !!Number(priceUSD) ? (
          <div
            data-testid="limitorder-token-list-item-account-info"
            className="token-account-info"
          >
            {balance ? (
              <span
                data-testid="limitorder-token-list-item-balance"
                className="balance"
              >
                {formatFormAmount(balance.toDecimalsFixed(), 4)}
              </span>
            ) : null}
            <span
              data-testid="limitorder-token-list-item-priceUSD"
              className="priceUSD"
            >
              ${formatAmount(priceUSD, 2)}
            </span>
          </div>
        ) : (
          <span>0</span>
        )}
        {custom ? <IconAdd /> : null}
      </div>
    </ListItem>
  )
}

export default TokenListItem

const ListItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgSurface};
  }

  .item-right {
    border-bottom: 0.5px solid ${({ theme }) => theme.colors.borderDefault};
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    padding: 17px 0;
    padding-right: 17.5px;
    min-height: 70px;
    margin-left: 8px;
  }

  .token-symbol {
    font-size: 17px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.textTitle};
    flex: 1;
  }

  .token-name {
    font-weight: 300;
    font-size: 13px;
    line-height: 17px;
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  .token-address {
    font-weight: 300;
    font-size: 13px;
    line-height: 17px;
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  .token-user-add {
    font-weight: 300;
    font-size: 13px;
    line-height: 17px;
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  .token-account-info {
    display: inline-flex;
    flex-direction: column;
    text-align: right;
  }

  .balance {
    font-weight: 500;
    font-size: 17px;
    line-height: 15px;
    color: ${({ theme }) => theme.colors.textTitle};
    margin: 0;
  }

  .priceUSD {
    font-weight: normal;
    font-size: 13px;
    line-height: 11px;
    color: ${({ theme }) => theme.colors.textTertiary};
    margin-top: 9px;
  }
`
